import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckIcon from "@mui/icons-material/Check";
import moment from "moment";
import { StatusChip } from "./StatusBadge";
import  { MARK_AS_COMPLETE } from "../../../core/Constant";
import openExternalUrl from "../../../core/openExternalUrl";

const ClassDetailsModal = ({
  open,
  onClose,
  selectedMeeting,
  onStartClass,
  onMarkCompleted,
  onEditClass,
  onDeleteClass,
}) => {
  const [copied, setCopied] = useState(false);

  if (!selectedMeeting) return null;

  const isCompleted = selectedMeeting.is_completed === MARK_AS_COMPLETE.ON;
  const isDisabledClass = selectedMeeting.status === MARK_AS_COMPLETE.OFF;

  const isActionDisabled = isDisabledClass || isCompleted;

  const handleCopyUrl = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 1,
          maxWidth: 740,
        },
      }}
    >
      <DialogTitle
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 3, pt: 1.5, px: 2 }}
      >
        <Typography variant="h4" fontWeight={700} color="#1E293B" fontSize="1.9rem">
          Scheduled Class Details
        </Typography>
        <IconButton onClick={onClose} size="42px" sx={{ color: "#e7123d" }}>
          <CloseIcon fontSize="42px" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Class Name */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Class Name
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#0F172A" fontWeight={700}>
                {selectedMeeting.name || "N/A"}
              </Typography>
            </Grid>

            {/* Course */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Course
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {selectedMeeting.courses?.map((c) => c.name).join(", ") ||
                  selectedMeeting.course_name ||
                  "N/A"}
              </Typography>
            </Grid>

            {/* Class Type */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Class Type
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {selectedMeeting.is_recurring === 1 ? "Recurring" : "One-Time"}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Status
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <StatusChip item={selectedMeeting} />
            </Grid>

            {/* Date & Time */}
            <Grid item xs={4} sx={{ alignSelf: "flex-start", pt: 1 }}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography variant="body2" color="#334155" fontWeight={600}>
                  {selectedMeeting.date
                    ? moment(selectedMeeting.date).format("DD MMMM YYYY")
                    : "N/A"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography variant="body2" color="#334155" fontWeight={500}>
                  {selectedMeeting.newTime || selectedMeeting.time || "N/A"} (
                  {selectedMeeting.duration || 60} Minutes)
                </Typography>
              </Box>
            </Grid>

            {/* Time Zone */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Time Zone
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {selectedMeeting.timezone || "Asia/Kolkata"}
              </Typography>
            </Grid>

            {/* Note */}
            <Grid item xs={4}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Webinar Note
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color="#334155" fontWeight={500}>
                {selectedMeeting.note || "N/A"}
              </Typography>
            </Grid>

            {/* Meeting URL */}
            <Grid item xs={4} sx={{ alignSelf: "flex-start" }}>
              <Typography variant="body2" color="#64748B" fontWeight={500}>
                Meeting URL
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                  variant="body2"
                  component="a"
                  href={selectedMeeting.join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#7C2DF4",
                    wordBreak: "break-all",
                    textDecoration: "none",
                    fontSize: "0.85rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {selectedMeeting.join_url || "N/A"}
                </Typography>
                {selectedMeeting.join_url && (
                  <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyUrl(selectedMeeting.join_url)}
                      sx={{ color: "#94A3B8", p: 0.5 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box display="flex" gap={1.5} mt={1}>
            <Button
              variant="contained"
              disabled={isActionDisabled}
              startIcon={!isActionDisabled ? <PlayArrowIcon /> : null}
              onClick={() => {
                if (isActionDisabled) return;
                if (onStartClass) onStartClass(selectedMeeting);
                else if (selectedMeeting.join_url) {
                  openExternalUrl(selectedMeeting.join_url);
                }
              }}
              sx={{
                bgcolor: isActionDisabled ? "#E2E8F0" : "#6E3FF3",
                color: isActionDisabled ? "#94A3B8" : "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: isActionDisabled ? "#E2E8F0" : "#5B2ECC",
                  boxShadow: "none",
                },
              }}
            >
              {isCompleted ? "End" : isDisabledClass ? "End" : "Start Class"}
            </Button>

            <Button
              variant="outlined"
              startIcon={<CheckIcon />}
              disabled={isActionDisabled}
              onClick={() => {
                if (isActionDisabled) return;
                if (onMarkCompleted) onMarkCompleted(selectedMeeting.id);
              }}
              sx={{
                borderColor: "#E2E8F0",
                color: isActionDisabled ? "#94A3B8"
                : "#475569",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                "&:hover": { borderColor: "#CBD5E1", bgcolor: "#F8FAFC" },
              }}
            >
              {isCompleted ? "Completed" : "Mark as Completed"}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 1, justifyContent: "flex-end", gap: 1 }}>
        <Button
          disabled={isActionDisabled}
          onClick={() => {
            if (isActionDisabled) return;
            const meetingId = selectedMeeting.id;
            onClose();
            if (onEditClass) onEditClass(meetingId);
          }}
          sx={{
            color: isActionDisabled ? "#94A3B8" : "#475569",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "auto",
            px: 1.5,
          }}
        >
          Edit
        </Button>

        <Button
          disabled={isActionDisabled}
          onClick={() => {
            if (isActionDisabled) return;
            const meetingId = selectedMeeting.id;
            onClose();
            if (onDeleteClass) onDeleteClass(meetingId);
          }}
          sx={{
            color: isActionDisabled ? "#94A3B8" : "#EF4444",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "auto",
            px: 1.5,
          }}
        >
          Delete
        </Button>

        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: "#CBD5E1",
            color: "#334155",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            py: 0.5,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClassDetailsModal;